import { appNow } from "./appClock";
import type { CompanyMxBase } from "./types";

const MX_ICAO_RE = /^[A-Z0-9]{3,4}$/;
export const MX_BASE_DESCRIPTION_MAX = 240;

/** Uppercase ICAO, digits and letters only, at most 4 characters. */
export function normalizeMxBaseIcao(raw: string): string {
  return raw.trim().toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 4);
}

export function mxBaseIcaoError(raw: string): string | null {
  const icao = normalizeMxBaseIcao(raw);
  if (!icao) return "ICAO is required.";
  if (!MX_ICAO_RE.test(icao)) return "ICAO must be 3 or 4 letters or digits.";
  return null;
}

export function mxBaseDescriptionError(raw: string): string | null {
  const description = raw.trim();
  if (!description) return "Say what this MX base is for.";
  if (description.length > MX_BASE_DESCRIPTION_MAX) {
    return `Description must be ${MX_BASE_DESCRIPTION_MAX} characters or fewer.`;
  }
  return null;
}

/** Older companies with no field stay empty. Invalid rows are dropped. */
export function hydrateMxBases(raw: unknown): CompanyMxBase[] {
  if (!Array.isArray(raw)) return [];
  const out: CompanyMxBase[] = [];
  const seen = new Set<string>();
  for (const row of raw) {
    if (!row || typeof row !== "object") continue;
    const rec = row as Partial<CompanyMxBase>;
    const icao = normalizeMxBaseIcao(typeof rec.icao === "string" ? rec.icao : "");
    if (!MX_ICAO_RE.test(icao) || seen.has(icao)) continue;
    const description =
      typeof rec.description === "string" ? rec.description.trim().slice(0, MX_BASE_DESCRIPTION_MAX) : "";
    const id = typeof rec.id === "string" && rec.id.trim() ? rec.id.trim() : `mx-${icao.toLowerCase()}`;
    seen.add(icao);
    out.push({ id, icao, description });
  }
  return out;
}

export type MxBaseWriteResult = { ok: true; bases: CompanyMxBase[] } | { ok: false; error: string };

function checkedFields(input: { icao: string; description: string }): MxBaseWriteResult | { icao: string; description: string } {
  const icaoErr = mxBaseIcaoError(input.icao);
  if (icaoErr) return { ok: false, error: icaoErr };
  const descErr = mxBaseDescriptionError(input.description);
  if (descErr) return { ok: false, error: descErr };
  return { icao: normalizeMxBaseIcao(input.icao), description: input.description.trim() };
}

export function addMxBase(
  bases: readonly CompanyMxBase[],
  input: { icao: string; description: string },
  now = appNow(),
): MxBaseWriteResult {
  const fields = checkedFields(input);
  if ("ok" in fields) return fields;
  if (bases.some((row) => row.icao === fields.icao)) {
    return { ok: false, error: `${fields.icao} is already an MX base.` };
  }
  const id = `mx-${fields.icao.toLowerCase()}-${now.getTime().toString(36)}-${Math.floor(Math.random() * 1e4).toString(36)}`;
  return { ok: true, bases: [...bases, { id, icao: fields.icao, description: fields.description }] };
}

export function updateMxBase(
  bases: readonly CompanyMxBase[],
  id: string,
  input: { icao: string; description: string },
): MxBaseWriteResult {
  const current = bases.find((row) => row.id === id);
  if (!current) return { ok: false, error: "MX base is not on this company." };
  const fields = checkedFields(input);
  if ("ok" in fields) return fields;
  if (bases.some((row) => row.id !== id && row.icao === fields.icao)) {
    return { ok: false, error: `${fields.icao} is already an MX base.` };
  }
  return {
    ok: true,
    bases: bases.map((row) => (row.id === id ? { ...row, icao: fields.icao, description: fields.description } : row)),
  };
}

export function removeMxBase(bases: readonly CompanyMxBase[], id: string): CompanyMxBase[] {
  return bases.filter((row) => row.id !== id);
}

export function mxBaseById(bases: readonly CompanyMxBase[] | undefined, id: string): CompanyMxBase | undefined {
  return (bases ?? []).find((row) => row.id === id);
}

/** Match a stored id or an ICAO the dispatcher (or Megan) typed. */
export function resolveCompanyMxBase(
  bases: readonly CompanyMxBase[] | undefined,
  hint: string,
): CompanyMxBase | undefined {
  const trimmed = hint.trim();
  if (!trimmed) return undefined;
  const list = bases ?? [];
  const byId = list.find((row) => row.id === trimmed);
  if (byId) return byId;
  const icao = normalizeMxBaseIcao(trimmed);
  if (!icao) return undefined;
  return list.find((row) => row.icao === icao);
}
